/* Curated reviews: native scrolling and disclosures work without JavaScript. */
if (!customElements.get('review-showcase')) {
  customElements.define('review-showcase', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-review-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.cards = Array.from(this.querySelectorAll('[data-review-card]'));
      this.filters = Array.from(this.querySelectorAll('[data-review-filter]'));
      this.previous = this.querySelector('[data-review-prev]');
      this.next = this.querySelector('[data-review-next]');
      this.status = this.querySelector('[data-review-status]');
      this.filters.forEach((button) => button.addEventListener('click', () => {
        this.filter(button.dataset.reviewFilter);
      }, { signal }));
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update(false));
        // Fallback for browsers without scrollend. Keep the count stable in motion.
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.update(), 150);
      }, { signal, passive: true });
      this.track.addEventListener('scrollend', () => {
        clearTimeout(this.scrollTimer);
        this.update();
      }, { signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        }
      }, { signal });
      // Theme editor selections reveal the chosen block, including inactive drafts.
      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-review-card]');
        if (!card) return;
        this.filter('');
        this.reveal(card, 'instant');
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.classList.add('is-ready');
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.scrollTimer);
    }

    filter(topic) {
      clearTimeout(this.scrollTimer);
      this.filters.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.reviewFilter === topic));
      });
      this.cards.forEach((card) => { card.hidden = Boolean(topic && card.dataset.topic !== topic); });
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
    }

    visibleCards() { return this.cards.filter((card) => !card.hidden); }

    reveal(card, behavior) {
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: rtl ? cardRect.right - trackRect.right : cardRect.left - trackRect.left, behavior });
    }

    move(direction) {
      if ((direction < 0 ? this.previous : this.next).getAttribute('aria-disabled') === 'true') return;
      const cards = this.visibleCards();
      if (!cards.length) return;
      const bounds = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const first = cards.findIndex((card) => {
        const rect = card.getBoundingClientRect();
        return rtl ? rect.right <= bounds.right + 2 : rect.left >= bounds.left - 2;
      });
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const page = Math.max(1, Math.floor((bounds.width + gap) / (cards[0].getBoundingClientRect().width + gap)));
      const index = Math.max(0, Math.min(cards.length - 1, Math.max(0, first) + direction * page));
      this.reveal(cards[index], matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
    }

    update(updateStatus = true) {
      const cards = this.visibleCards();
      const bounds = this.track.getBoundingClientRect();
      const inView = cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
        .filter(({ rect }) => rect.left >= bounds.left - 2 && rect.right <= bounds.right + 2);
      // Between snap points no card may be fully visible. That is not review 1.
      if (updateStatus && !cards.length) {
        this.status.textContent = '0 / 0';
      } else if (updateStatus && inView.length) {
        const first = inView[0].index;
        const last = inView[inView.length - 1].index;
        const label = `${first + 1}${last > first ? `–${last + 1}` : ''} / ${cards.length}`;
        if (this.status.textContent !== label) this.status.textContent = label;
      }
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const start = cards[0]?.getBoundingClientRect();
      const end = cards[cards.length - 1]?.getBoundingClientRect();
      const atStart = !start || (rtl ? start.right <= bounds.right + 2 : start.left >= bounds.left - 2);
      const atEnd = !end || (rtl ? end.left >= bounds.left - 2 : end.right <= bounds.right + 2);
      // aria-disabled keeps focus on a control when it reaches the end of the rail.
      this.previous.setAttribute('aria-disabled', String(!overflow || atStart));
      this.next.setAttribute('aria-disabled', String(!overflow || atEnd));
      this.querySelector('[data-review-navigation]').hidden = !overflow;
    }
  });
}
